import CheckIcon from '@mui/icons-material/Check';
import {
  buildQuoteCustomerPayload,
  holderValuesToKeep,
  isoToBirthDateMask,
  updateHolderBeforePayment
} from './utils/holderData';
import { yupResolver } from '@hookform/resolvers/yup';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildQuoteSubscriptionRows } from '~/components/PersonalCoverage/JourneyQuote2026/buildQuoteSubscriptionRows';
import JourneyQuoteInsuredHeader from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteInsuredHeader';
import JourneyQuoteSummaryColumn from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteSummaryColumn';
import { JourneyLoadingScreen, StandardJourneyBrandedScreen } from '~/components/StandardJourney';
import { usePaymentGatewayPublicKey } from '~/hooks/usePaymentGatewayPublicKey';
import { useQuoteData } from '~/hooks/useQuoteData';
import { get422ErrorMessage, getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Checkbox, FormControlLabel, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { Focused } from 'react-credit-cards-2';
import { AiOutlineCreditCard } from 'react-icons/ai';
import { FaPix } from 'react-icons/fa6';
import api, { createAuthenticatedEventSource } from '~/services/api';
import type { AuthenticatedEventSourceLike } from '~/services/api';
import { trackConversion } from '~/services/gtag';
import useStore, { storeState } from '~/store';
import { priceFormatter } from '~/utils/priceFormatter';
import CardForm from './components/CardForm';
import MethodButton from './components/MethodButton';
import PaymentJourneyStepCard from './components/PaymentJourneyStepCard';
import { PIX_SCAN_TIME_SECONDS } from './constants';
import { readMembershipFeePix } from './pixResponse';
import { PixPayment } from './components/PixPayment';
import schema from './validation';
import { getPaymentStoryUiOverride } from './stories/paymentStoryUiOverride';
import type { Inputs } from './types';
import { buildPersonalCoverageQuoteUrl } from '~/pages/PersonalCoverage/newJourney/buildPersonalCoverageQuoteUrl';
import searchNameEquipment from '~/utils/searchNameEquipment';

export type { Inputs } from './types';

interface CreateTokenCardResponse {
  id: string;
  type: string;
  created_at: string;
  expires_at: string;
  card: {
    first_six_digits: string;
    last_four_digits: string;
    holder_name: string;
    exp_month: number;
    exp_year: number;
    brand: string;
  };
}

interface CreditCardAddress {
  number: string;
  zipcode: string;
  city: string;
  state: string;
  neighborhood: string;
  street: string;
  additional_details: string;
}

interface CreditCardPayment {
  cardToken: string;
  address: CreditCardAddress;
}

interface MembershipFeePaymentRequest {
  payment_method: 'credit_card';
  organization_id?: string;
  creditCard: CreditCardPayment;
}

type PagarmeResponseCardError = {
  message: string;
  errors: {
    'request.card': string[];
  };
};

const PAGARME_URL = import.meta.env.VITE_PAGARME_API_URL;

export default function HelpZipcodePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const storyUi = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
  const showCardFromQuery = useMediaQuery('(min-width:420px)');
  const showCard = storyUi?.forceShowCard ?? showCardFromQuery;
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const distribuitorParam = searchParams.get('dis');
  const orgParam = searchParams.get('org');
  const sellerParam = searchParams.get('seller');
  const quoteIdParam = searchParams.get('quote');
  const [installmentsMethodSelected, setInstallmentsMethodSelected] = useState<string | null>(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    return ui?.installmentsMethodSelected ?? null;
  });
  const [membershipMethodSelected, setMembershipMethodSelected] = useState<string | null>(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    return ui?.membershipMethodSelected ?? null;
  });
  const [pixValue, setPixValue] = useState<string | null>(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    return ui?.pixCopyPaste ?? null;
  });
  const [pixId, setPixId] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    return ui?.pixTimeLeft ?? PIX_SCAN_TIME_SECONDS;
  });
  const [pixGenerating, setPixGenerating] = useState(false);
  const [eventSourceConnection, setEventSourceConnection] = useState<AuthenticatedEventSourceLike | null>(null);
  const [pixPaymentConfirmed, setPixPaymentConfirmed] = useState<boolean>(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    return ui?.pixPaymentConfirmed ?? false;
  });
  const [reuseCardData, setReuseCardData] = useState(false);

  // Hook para recuperar dados da cotação
  const { quoteLoading, quoteData } = useQuoteData({
    redirectToHome: false,
    quoteParamName: 'quote',
    skipFetch: import.meta.env.VITE_STORYBOOK === 'true'
  });

  const formMethods = useForm<Inputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: (storyUi?.cardFormDefaults ?? {}) as Partial<Inputs>
  });
  const { setValue, watch, clearErrors, reset, trigger, getValues } = formMethods;
  const [loading, setLoading] = useState(false);
  const {
    equipment,
    quoteResponse,
    quoteAsyncPostPayment,
    quotes,
    setZipcode,
    setState,
    setCity,
    setNeighborhood,
    setStreet,
    setNumber,
    totalValue,
    products,
    planOptions,
    customer,
    equipmentValue,
    membershipFee,
    organizationIdFromStore,
    serviceCharge,
    insured,
    quoteForm,
    quoteAsyncPostCustomer,
    quoteFormName
  } = useStore((store) => ({
    customer: store.customer,
    quotes: store.quotes,
    equipment: store.quote.equipment,
    quoteResponse: store.quoteResponse,
    quoteAsyncPostPayment: store.quoteAsyncPostPayment,
    equipmentValue: store.quote.equipmentValue,
    setZipcode: store.setZipcode,
    setState: store.setState,
    setCity: store.setCity,
    setNeighborhood: store.setNeighborhood,
    setStreet: store.setStreet,
    setNumber: store.setNumber,
    totalValue: store.totalValue,
    products: store.products,
    planOptions: store.planOptions.payment,
    membershipFee: store.quoteResponse?.membership_fee,
    organizationIdFromStore: store.quote.organization_id,
    serviceCharge: store.serviceCharge,
    insured: store.insured,
    quoteForm: store.quote,
    quoteAsyncPostCustomer: store.quoteAsyncPostCustomer,
    quoteFormName: store.quote.name
  }));
  const organizationIdForPayment = orgParam ?? organizationIdFromStore ?? null;
  const {
    publicKey: paymentGatewayPublicKeyFromOrg,
    loading: paymentGatewayKeyLoading,
    error: paymentGatewayKeyError
  } = usePaymentGatewayPublicKey(organizationIdForPayment);

  const { setMembershipFeeCard, setInstallmentsCard, quoteCard } = useStore((store) => ({
    setMembershipFeeCard: store.setMembershipFeeCard,
    setInstallmentsCard: store.setInstallmentsCard,
    quoteCard: store.quoteCard
  }));

  // ID da cotação para chamadas à API: prioriza quote_hash (usado na URL da API v2), depois query param (F5), depois quote_id
  const quoteIdForApi = quoteResponse?.quote_hash ?? quoteIdParam ?? quoteResponse?.quote_id ?? '';

  const [stepSelected, setStepSelected] = useState<string>(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    if (ui?.stepSelected) return ui.stepSelected;
    const po = useStore.getState().planOptions.payment;
    return po.membership_fee.methods.length === 0 ? 'installments' : 'membership_fee';
  });
  const [paymentStepsState, setPaymentStepsState] = useState(() => {
    const ui = import.meta.env.VITE_STORYBOOK === 'true' ? getPaymentStoryUiOverride() : null;
    return {
      membership_fee: ui?.paymentStepsState?.membership_fee ?? false,
      installments: ui?.paymentStepsState?.installments ?? false
    };
  });

  // Efeito para configurar estado inicial baseado nos dados da cotação
  useEffect(() => {
    if (quoteData && !quoteLoading) {
      // Configurar métodos de pagamento baseado nos dados
      let defaultMembershipMethod = 'pix';
      let defaultInstallmentsMethod = 'credit_card';

      if (
        quoteData.payment?.membership_fee?.status === 2 ||
        quoteData.additionalInfo?.membershipFee_payment_status === 2
      ) {
        setPaymentStepsState((prevState) => ({
          ...prevState,
          membership_fee: true
        }));
        setStepSelected('installments');
        setMembershipMethodSelected(defaultMembershipMethod);
        setInstallmentsMethodSelected(defaultInstallmentsMethod);
        setPixPaymentConfirmed(true);
      } else {
        if (quoteData.is_control_membership_fee_payment_system) {
          setStepSelected('membership_fee');
          setMembershipMethodSelected(defaultMembershipMethod);
        } else {
          setStepSelected('installments');
          setInstallmentsMethodSelected(defaultInstallmentsMethod);
        }
      }
    }
  }, [quoteData, quoteLoading]);

  useEffect(() => {
    if (!quoteLoading && quotes.length === 0 && !quoteIdParam) {
      const diagnosticInfo = {
        quotesArray: quotes,
        quotesLength: quotes.length,
        quotesType: Array.isArray(quotes) ? 'array' : typeof quotes,

        urlParams: {
          planParam: planParam || 'não informado',
          distribuitorParam: distribuitorParam || 'não informado',
          quoteIdParam: quoteIdParam || 'não informado (esperado quando quotes.length = 0)',
          fullUrl: window.location.href
        },

        loadingState: {
          quoteLoading,
          quoteDataExists: !!quoteData,
          quoteDataId: quoteData?.quote_id || 'não disponível'
        },

        storeState: {
          equipment: equipment || 'não selecionado',
          equipmentValue: equipmentValue || 'não informado',
          totalValue: totalValue || 'não calculado',
          productsLength: products?.length || 0,
          customerData: customer ? 'presente' : 'ausente',
          quoteResponseExists: !!quoteResponse,
          quoteResponseId: quoteResponse?.quote_id || 'não disponível'
        },

        paymentMethods: {
          membershipFeeMethods: planOptions?.membership_fee?.methods || [],
          installmentsMethods: planOptions?.installments?.methods || [],
          hasMembershipFee: !!membershipFee
        },

        sessionInfo: {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'acesso direto'
        }
      };

      console.error('🚨 ERRO CRÍTICO: Array de quotes vazio após carregamento completo', diagnosticInfo);

      reportError(
        'Array de quotes vazio na página de pagamento - Estado da aplicação sem cotações válidas',
        new Error('Empty quotes array after complete loading'),
        {
          context: 'Payment_empty_quotes_detailed',
          severity: 'critical',
          ...diagnosticInfo
        }
      );

      storeState.addToast({
        title: 'Erro ao carregar cotações',
        message:
          'Não foi possível carregar as cotações necessárias. Por favor, refaça a cotação ou verifique se o link está correto.',
        type: 'error'
      });
    }
  }, [
    quotes,
    quoteLoading,
    quoteIdParam,
    equipment,
    quoteData,
    totalValue,
    products,
    customer,
    quoteResponse,
    planOptions,
    membershipFee
  ]);

  const zipcode = watch('zipcode');

  const [paymentError] = useState<boolean | string>(false);
  const [focused, setfocused] = useState<Focused>('number');
  const [checkedTermOfContract, setCheckedTermOfContract] = useState<boolean>(false);
  const hasMembershipFee = planOptions.membership_fee.methods.length > 0;
  const paymentMethodLabels: Record<string, string> = {
    pix: 'Pix',
    credit_card: 'Cartão de crédito',
    debit_card: 'Cartão de débito',
    boleto: 'Boleto'
  };

  const personalCoveragePath = useMemo(
    () =>
      buildPersonalCoverageQuoteUrl({
        searchParams,
        planIdFromStore: planParam,
        organizationId: orgParam,
        seller: sellerParam,
        quoteHash: quoteIdParam ?? undefined
      }),
    [orgParam, planParam, quoteIdParam, searchParams, sellerParam]
  );

  // «Dados do titular»: parte já preenchido com quem criou a cotação; o utilizador corrige se o
  // cartão for de outra pessoa. Só escreve em campos ainda vazios, para não apagar edições.
  useEffect(() => {
    const prefill: Array<[keyof Inputs, string]> = [
      ['holderName', insured.name || quoteFormName || customer?.name || ''],
      ['holderDocument', insured.document || quoteForm.cpf || ''],
      ['holderBirthDate', isoToBirthDateMask(insured.birthdate || quoteForm.birthDate)],
      ['holderPhone', insured.mobile_phone || quoteForm.phoneNumber || ''],
      ['holderEmail', insured.email || quoteForm.email || '']
    ];
    prefill.forEach(([field, value]) => {
      if (value && !getValues(field)) setValue(field, value, { shouldValidate: false });
    });
  }, [
    customer?.name,
    getValues,
    insured.birthdate,
    insured.document,
    insured.email,
    insured.mobile_phone,
    insured.name,
    quoteForm.birthDate,
    quoteForm.cpf,
    quoteForm.email,
    quoteForm.phoneNumber,
    quoteFormName,
    setValue
  ]);

  const displayInsuredName = useMemo(() => {
    const n = (insured.social_name || insured.name || quoteFormName || customer?.name || '').trim();
    return n || null;
  }, [insured.social_name, insured.name, quoteFormName, customer?.name]);

  const subscriptionRows = useMemo(
    () =>
      buildQuoteSubscriptionRows({
        flat_rate: quoteResponse?.flat_rate,
        plan_club_name: quoteResponse?.plan_club_name,
        plan_has_club: quoteResponse?.plan_has_club,
        products
      }),
    [quoteResponse?.flat_rate, quoteResponse?.plan_club_name, quoteResponse?.plan_has_club, products]
  );

  const isOnlyProduct9 = products?.length === 1 && String(products[0]?.product_id) === '9';

  const equipmentLabel = equipment ? searchNameEquipment(equipment) : null;
  const equipmentValueNumber =
    equipmentValue != null && Number.isFinite(Number(equipmentValue)) ? Number(equipmentValue) : null;

  const membershipStepTitle = useMemo(() => {
    return 'Pague sua taxa de adesão.';
  }, []);

  const installmentsSectionLocked = hasMembershipFee && !paymentStepsState.membership_fee;

  useEffect(() => {
    if (!quoteLoading && !equipment && !quoteData && quoteIdParam) {
      console.error('Dados necessários não encontrados após carregamento:', {
        equipment,
        quoteData,
        quoteIdParam,
        planParam,
        distribuitorParam
      });

      reportError('Dados necessários não encontrados na página de pagamento', new Error('Missing required data'), {
        context: 'Payment_missing_data',
        equipment,
        hasQuoteData: !!quoteData,
        quoteIdParam,
        planParam,
        distribuitorParam,
        quoteLoading
      });

      storeState.addToast({
        title: 'Erro ao carregar dados do pagamento',
        message:
          'Não foi possível carregar os dados necessários para continuar com o pagamento. Por favor, tente novamente.',
        type: 'error'
      });
    }
  }, [equipment, quoteData, quoteLoading, quoteIdParam]);

  useEffect(() => {
    const cleanZipcode = zipcode?.replace('-', '').trim();

    if (cleanZipcode && cleanZipcode.length === 8) {
      api.get(`ZipCodes?zipcode=${cleanZipcode}`).then(({ data: { data } }) => {
        console.log('Dados do CEP recebidos:', data);

        Object.entries(data).forEach(([key, value]) => {
          setValue(key as keyof Inputs, value as string, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        });

        clearErrors('zipcode');
        setTimeout(() => {
          trigger(['state', 'city', 'neighborhood', 'street']);
        }, 100);
      });
    }
  }, [zipcode, setValue, clearErrors, trigger]);

  useEffect(() => {
    if (stepSelected === 'membership_fee' && planOptions.membership_fee.methods.length === 1) {
      setMembershipMethodSelected(planOptions.membership_fee.methods[0]);
    } else if (stepSelected === 'installments' && planOptions.installments.methods.length === 1) {
      setInstallmentsMethodSelected(planOptions.installments.methods[0]);
    }
  }, [stepSelected, planOptions]);

  /** Sem public_key na cotação: obrigatório obter via /Organizations/:id/payment_gateway */
  const needsOrgPaymentGateway = !!organizationIdForPayment && !planOptions?.payment_gateway?.public_key;
  useEffect(() => {
    if (needsOrgPaymentGateway && paymentGatewayKeyError) {
      storeState.addToast({
        title: 'Configuração de pagamento',
        message:
          'Não foi possível carregar a chave de pagamento da organização. Verifique o parâmetro org na URL ou tente novamente.',
        type: 'error'
      });
    }
  }, [needsOrgPaymentGateway, paymentGatewayKeyError]);

  type PaymentStep = keyof typeof paymentStepsState;

  const resetPixPaymentState = () => {
    if (eventSourceConnection) {
      console.log('Fechando conexão EventSource existente');
      eventSourceConnection.close();
      setEventSourceConnection(null);
    }

    setPixValue(null);
    setTimeLeft(PIX_SCAN_TIME_SECONDS);
    setLoading(false);
    setPixPaymentConfirmed(false);
  };

  const waitForPixPaymentConfirmation = async (pixId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        if (timeLeft <= 0) {
          reject(new Error('Tempo de espera expirado.'));
          return;
        }

        const organizationId = orgParam ?? organizationIdFromStore ?? undefined;
        console.log(
          `Iniciando nova conexão EventSource para o ID: ${pixId}${organizationId ? ` (org: ${organizationId})` : ''}`
        );
        const eventSource = createAuthenticatedEventSource(`/Orders/${pixId}/status_update`, {
          ...(organizationId && { organization_id: organizationId })
        });
        setEventSourceConnection(eventSource);

        eventSource.onopen = () => {
          console.log('Conexão com EventSource estabelecida com sucesso.');
        };

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data);
          console.log('Mensagem recebida:', data);

          switch (data.status) {
            case 1:
              console.log('Pagamento pendente.');
              break;
            case 2:
              console.log('Pagamento confirmado. Fechando conexão.');
              eventSource.close();
              setEventSourceConnection(null);
              setPixPaymentConfirmed(true);
              resolve();
              break;
            case 3:
              console.error('Pagamento cancelado. Fechando conexão.');
              reportError('Pagamento via Pix cancelado', new Error('Pagamento cancelado'), {
                context: 'waitForPixPaymentConfirmation_payment_cancelled',
                pixId,
                status: 3
              });
              eventSource.close();
              setEventSourceConnection(null);
              reject(new Error('Pagamento cancelado.'));
              break;
            case 4:
              console.error('Falha no pagamento. Fechando conexão.');
              reportError('Falha no pagamento via Pix', new Error('Falha no pagamento'), {
                context: 'waitForPixPaymentConfirmation_payment_failed',
                pixId,
                status: 4
              });
              eventSource.close();
              setEventSourceConnection(null);
              reject(new Error('Falha no pagamento.'));
              break;
            default:
              console.error('Status desconhecido:', data.status);
              reportError(
                'Status desconhecido recebido do EventSource',
                new Error(`Status desconhecido: ${data.status}`),
                {
                  context: 'waitForPixPaymentConfirmation_unknown_status',
                  status: data.status,
                  pixId,
                  eventData: data
                }
              );
          }
        };

        eventSource.onerror = (error) => {
          console.error('Erro na conexão com EventSource:', error);
          reportError('Erro na conexão com EventSource durante verificação do Pix', error, {
            context: 'waitForPixPaymentConfirmation_eventSource_error',
            pixId,
            timeLeft
          });
          eventSource.close();
          setEventSourceConnection(null);
          reject(new Error('Erro ao verificar o status do pagamento via Pix.'));
        };
      } catch (error) {
        console.error('Erro ao criar EventSource:', error);
        reportError('Erro ao criar EventSource autenticado', error, {
          context: 'waitForPixPaymentConfirmation_create_eventSource',
          pixId,
          timeLeft
        });
        reject(new Error('Erro ao criar EventSource autenticado.'));
      }
    });
  };

  const handleConfirmStep = async (currentStep: PaymentStep) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (currentStep === 'membership_fee') {
        if (membershipMethodSelected === 'pix' && pixPaymentConfirmed) {
          setPaymentStepsState((prevState) => ({
            ...prevState,
            membership_fee: true
          }));
          setStepSelected('installments');
        } else if (membershipMethodSelected === 'credit_card') {
          // Para cartão de crédito, o submit é feito pelo form diretamente
          // Não fazemos nada aqui, o onSubmit já gerencia a transição
          console.log('Submit do cartão será feito pelo form');
        } else {
          storeState.addToast({
            title: 'Selecione uma forma de pagamento',
            message: 'Você precisa selecionar uma forma de pagamento para continuar.',
            type: 'error'
          });
        }
      } else if (currentStep === 'installments') {
        // Para installments com cartão, o submit é feito pelo form diretamente
        // Não fazemos nada aqui, o onSubmit já gerencia tudo
        console.log('Submit das parcelas será feito pelo form');
      }
    } catch (error) {
      console.error('Erro ao confirmar etapa:', error);
      reportError('Erro ao confirmar etapa de pagamento', error, {
        context: 'handleConfirmStep',
        currentStep,
        membershipMethodSelected,
        installmentsMethodSelected,
        stepSelected
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Só a cobrança no cartão — não a espera do Pix, que precisa do QR visível. */
  const [processingPayment, setProcessingPayment] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const expiryMonth = data.expiry.split('/')[0];
    const expiryYear = data.expiry.split('/')[1];

    const createTokenCardPayload = {
      type: 'card',
      card: {
        number: data.number.replaceAll(' ', '').trim(),
        exp_month: parseInt(expiryMonth),
        exp_year: parseInt(expiryYear),
        holder_name: data.name.trim(),
        cvv: data.cvv.trim()
      }
    };

    try {
      setLoading(true);
      setProcessingPayment(true);

      // «Dados do titular» → PUT /v2/Quotes/{hash}/customer (UpdateQuoteCustomerViewModel).
      // Corre antes do pagamento: se o titular divergir de quem criou a cotação, a
      // cobrança tem de sair já com os dados corretos. Se a gravação falhar, o pagamento
      // para e o toast da API aparece (antes seguia com o titular antigo).
      if (quoteIdForApi && data.holderDocument) {
        await updateHolderBeforePayment(
          () =>
            quoteAsyncPostCustomer(
              quoteIdForApi,
              buildQuoteCustomerPayload(data, {
                customerId: quoteForm.customer_id || null,
                organizationId: orgParam ?? organizationIdFromStore ?? null,
                socialName: quoteForm.isSocialName ? quoteFormName : null
              })
            ),
          (customerError) =>
            reportError('Erro ao atualizar titular da cotação', customerError as Error, {
              context: 'Payment_updateQuoteCustomer',
              quote: quoteIdForApi
            })
        );
      }

      const publicKeyFromPlan = planOptions.payment_gateway?.public_key ?? null;
      const publicKeyFromOrg = paymentGatewayPublicKeyFromOrg;
      /** Com `org` na URL, priorizar sempre a chave de GET /Organizations/:orgId/payment_gateway (evita token com pk padrão do plano). */
      const publicKey = organizationIdForPayment
        ? paymentGatewayKeyLoading
          ? null
          : publicKeyFromOrg ?? publicKeyFromPlan
        : publicKeyFromPlan ?? publicKeyFromOrg;
      if (!publicKey) {
        if (organizationIdForPayment && paymentGatewayKeyLoading) {
          storeState.addToast({
            title: 'Aguarde',
            message: 'Carregando configuração de pagamento. Tente novamente em instantes.',
            type: 'warning'
          });
        } else {
          storeState.addToast({
            title: 'Configuração de pagamento',
            message:
              'Chave de pagamento não disponível. Acesse pela organização (parâmetro org na URL) ou tente novamente.',
            type: 'error'
          });
        }
        setLoading(false);
        setIsSubmitting(false);
        return;
      }
      const createTokenCardResponse = await axios.post<CreateTokenCardResponse>(
        `${PAGARME_URL}/tokens?appId=${publicKey}`,
        {
          ...createTokenCardPayload
        }
      );

      if (membershipMethodSelected === 'credit_card' && stepSelected === 'membership_fee') {
        if (!quoteIdForApi) {
          storeState.addToast({
            title: 'Aguarde',
            message: 'Carregando dados da cotação. Tente novamente em instantes.',
            type: 'warning'
          });
          setLoading(false);
          setIsSubmitting(false);
          return;
        }
        const paymentRequest: MembershipFeePaymentRequest = {
          payment_method: 'credit_card',
          ...((orgParam ?? organizationIdFromStore) && {
            organization_id: (orgParam ?? organizationIdFromStore)!
          }),
          creditCard: {
            cardToken: createTokenCardResponse.data.id,
            address: {
              number: typeof data.numberAddress === 'string' ? data.numberAddress.trim() : data.numberAddress,
              zipcode: data.zipcode.replace('-', ''),
              city: typeof data.city === 'string' ? data.city.trim() : data.city,
              state: typeof data.state === 'string' ? data.state.trim() : data.state,
              neighborhood: typeof data.neighborhood === 'string' ? data.neighborhood.trim() : data.neighborhood,
              street: typeof data.street === 'string' ? data.street.trim() : data.street,
              additional_details:
                typeof data.additionalDetails === 'string'
                  ? data.additionalDetails.trim()
                  : data.additionalDetails || ''
            }
          }
        };

        await api.post(`Quotes/${quoteIdForApi}/membership_fee`, paymentRequest);

        setPaymentStepsState((prevState) => ({
          ...prevState,
          membership_fee: true
        }));
        setStepSelected('installments'); // Passa para o próximo passo
        // Limpa o cartão, mas mantém «Dados do titular» para a mensalidade (antes apagava tudo).
        reset(holderValuesToKeep(getValues()) as Partial<Inputs>);
      } else {
        const paymentMethod = installmentsMethodSelected === 'pix' ? 'pix' : 'credit_card';
        await quoteAsyncPostPayment({
          quote_id: quoteIdForApi,
          payment: {
            card_hash: createTokenCardResponse?.data?.id ?? '',
            payment_method: paymentMethod
          },
          tax_address: {
            number:
              typeof data.numberAddress === 'string' ? data.numberAddress.trim() : String(data.numberAddress ?? ''),
            additional_details:
              typeof data.additionalDetails === 'string' ? data.additionalDetails.trim() : data.additionalDetails || '',
            zipcode: data.zipcode.replace('-', ''),
            street: typeof data.street === 'string' ? data.street.trim() : data.street,
            neighborhood: typeof data.neighborhood === 'string' ? data.neighborhood.trim() : data.neighborhood,
            city: typeof data.city === 'string' ? data.city.trim() : data.city,
            state: typeof data.state === 'string' ? data.state.trim() : data.state
          },
          organization_id: orgParam ?? organizationIdFromStore ?? ''
        });

        var items: { item_id: string; item_name: string; quantity: number; price: number }[] = [];
        products.forEach((product) => {
          items.push({
            item_id: product.product_id,
            item_name: product.product_name,
            quantity: 1,
            price: product.product_price
          });
        });

        if (quoteResponse.plan_has_club) {
          items.push({
            item_id: '0',
            item_name: quoteResponse.plan_club_name,
            quantity: 1,
            price: quoteResponse.flat_rate
          });
        }

        trackConversion('AW-1003809936/yDi1CJDDmosaEJDZ094D', quoteResponse.total || 1);

        const queryTail = `${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${
          orgParam ? `&org=${orgParam}` : ''
        }${sellerParam ? `&seller=${sellerParam}` : ''}`;
        navigate(`/paymentConfirm/?plan=${planParam}${queryTail}`);
        reset(); // <-- Resetar apenas após sucesso
      }
    } catch (error: any) {
      setLoading(false);
      // Removido reset() daqui para não limpar o formulário em caso de erro

      // Log do erro com contexto
      const errorContext = logErrorWithContext(error, 'onSubmit_payment', {
        stepSelected,
        membershipMethodSelected,
        installmentsMethodSelected,
        quoteId: quoteResponse?.quote_id
      });

      // Tratamento especial para erros 422 com validação de cartão
      if (error.isAxiosError && error.response?.status === 422) {
        const axiosError = error as AxiosError<PagarmeResponseCardError>;
        const errorResponse = get422ErrorMessage(axiosError);

        storeState.addToast(errorResponse);

        // Log específico para Rollbar
        const errors = axiosError?.response?.data?.errors;
        const rollbarContext = errors?.['request.card'] ? 'onSubmit_422_card_error' : 'onSubmit_422_general_error';

        reportError(`Erro 422: ${errorResponse.title}`, axiosError, {
          status: axiosError.response?.status,
          data: {
            message: axiosError.response?.data.message,
            errors: axiosError.response?.data.errors,
            axiosRaw: axiosError.toJSON()
          },
          context: rollbarContext,
          errorType: errorContext.errorType
        });
      } else {
        // Usar o sistema unificado de tratamento de erros
        const errorResponse = getErrorMessage(error);

        storeState.addToast(errorResponse);

        // Log para Rollbar com contexto apropriado
        const rollbarContext = error.isAxiosError
          ? error.message === 'Network Error'
            ? 'onSubmit_networkError'
            : 'onSubmit_axios_error'
          : 'onSubmit_non_axios_error';

        reportError(`Erro durante pagamento: ${errorResponse.title}`, error, {
          status: error.response?.status,
          data: error.response?.data,
          context: rollbarContext,
          errorType: errorContext.errorType,
          originalMessage: error.message
        });
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
      setProcessingPayment(false);
    }

    setZipcode(data.zipcode);
    setState(data.state);
    setCity(data.city);
    setNeighborhood(data.neighborhood);
    setStreet(data.street);
    setNumber(data.numberAddress);
  };

  const handleMembershipFeeSubmit: SubmitHandler<Inputs> = async (data) => {
    // Log para verificar os dados enviados
    console.log('Dados enviados para setMembershipFeeCard:', data);

    const formattedData = {
      number: data.number.replaceAll(' ', '').trim(),
      cvv: data.cvv.trim(),
      expiry: data.expiry.trim(),
      name: data.name.trim(),
      zipcode: data.zipcode.replace('-', ''),
      state: data.state.trim(),
      city: data.city.trim(),
      neighborhood: data.neighborhood.trim(),
      street: data.street.trim(),
      numberAddress: data.numberAddress.trim(),
      additionalDetails: data.additionalDetails?.trim() || ''
    };

    console.log('Dados formatados para setMembershipFeeCard:', formattedData);

    setMembershipFeeCard(formattedData);

    console.log('Dados do cartão de adesão salvos na store:', formattedData);
    await onSubmit(data);
  };

  const handleInstallmentsSubmit: SubmitHandler<Inputs> = async (data) => {
    setInstallmentsCard(data);
    await onSubmit(data);
  };

  const handleReusedCardSubmit = async () => {
    console.log('Dados do cartão reutilizados');
    try {
      setLoading(true);
      setProcessingPayment(true);

      const reusedCardData = {
        number: quoteCard.membership_fee.number || '',
        name: quoteCard.membership_fee.name || '',
        expiry: quoteCard.membership_fee.expiry || '',
        cvv: quoteCard.membership_fee.cvv || '',
        zipcode: quoteCard.membership_fee.zipcode || '',
        numberAddress: quoteCard.membership_fee.numberAddress || '',
        street: quoteCard.membership_fee.street || '',
        neighborhood: quoteCard.membership_fee.neighborhood || '',
        city: quoteCard.membership_fee.city || '',
        state: quoteCard.membership_fee.state || '',
        additionalDetails: quoteCard.membership_fee.additionalDetails || '',
        // Titular já validado no passo da adesão; reaproveita o que está no formulário.
        holderName: getValues('holderName'),
        holderDocument: getValues('holderDocument'),
        holderBirthDate: getValues('holderBirthDate'),
        holderPhone: getValues('holderPhone'),
        holderEmail: getValues('holderEmail')
      };

      await handleInstallmentsSubmit(reusedCardData);

      console.log('Dados do cartão reutilizados enviados com sucesso:', reusedCardData);
    } catch (error) {
      console.error('Erro ao reutilizar os dados do cartão:', error);
      reportError('Erro ao reutilizar dados do cartão', error, {
        context: 'handleReusedCardSubmit',
        hasQuoteCard: !!quoteCard,
        hasMembershipFee: !!quoteCard?.membership_fee
      });
      storeState.addToast({
        title: 'Erro ao reutilizar os dados do cartão',
        message: 'Não foi possível efetuar o pagamento com os dados reutilizados. Tente novamente.',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setProcessingPayment(false);
    }
  };

  const generatePixQRCode = async () => {
    try {
      if (!quoteIdForApi) {
        storeState.addToast({
          title: 'Aguarde',
          message: 'Carregando dados da cotação. Tente novamente em instantes.',
          type: 'warning'
        });
        return;
      }
      if (pixValue || eventSourceConnection) {
        console.log('QR Code já gerado ou verificação em andamento. Ignorando nova chamada.');
        return;
      }

      resetPixPaymentState();

      setPixGenerating(true);
      const response = await api.v2!.post(`Quotes/${quoteIdForApi}/membership_fee`, {
        payment_method: 'pix',
        ...((orgParam ?? organizationIdFromStore) && {
          organization_id: orgParam ?? organizationIdFromStore ?? ''
        })
      });
      setPixGenerating(false);

      if (response.status === 200) {
        if (response.data?.data.message) {
          storeState.addToast({
            title: 'Erro ao gerar QR Code',
            message: response.data.data.message,
            type: 'warning'
          });

          if (response.data.data.status === 2) {
            console.log('Validando fluxo de pagamento para a taxa de adesão.');
            setPaymentStepsState((prevState) => ({
              ...prevState,
              membership_fee: true
            }));
            setStepSelected('installments');
            setPixValue(''); // Gera um QR Code vazio apenas para exibição
            setPixPaymentConfirmed(true); // Habilita o botão de pular para o próximo passo
          }
          return;
        }
        const pix = readMembershipFeePix(response.data?.data);
        if (!pix) throw new Error('Resposta do Pix sem QR ou pedido.');
        setPixValue(pix.qrCode);
        setPixId(pix.orderId);
        setTimeLeft(PIX_SCAN_TIME_SECONDS);

        // Sem toast «Pagamento via Pix iniciado»: o carregamento vive no botão «Gerar QR code PIX» e o QR aparece logo a seguir.
        await waitForPixPaymentConfirmation(pix.orderId);
      }
    } catch (error) {
      setPixGenerating(false);
      console.error('Erro ao gerar QR Code:', error);
      reportError('Erro ao gerar QR Code do Pix', error, {
        context: 'generatePixQRCode',
        quoteId: quoteResponse?.quote_id,
        hasPixValue: !!pixValue,
        hasEventSourceConnection: !!eventSourceConnection
      });
      storeState.addToast({
        title: 'Erro ao gerar QR Code',
        message: 'Não foi possível gerar o código Pix. Tente novamente.',
        type: 'error'
      });
      resetPixPaymentState();
    }
  };

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => Math.max(prev - 1, 0));
      }, 1000);
    } else if (timeLeft === 0) {
      console.log('Timer expirado. Resetando estado do PIX.');
      resetPixPaymentState();
    }
    return () => clearInterval(timer);
  }, [timeLeft]);

  if (processingPayment) {
    return (
      <StandardJourneyBrandedScreen
        appId="journey-payment-processing"
        contentLayout="fullscreen"
        progressPercent={(2 / 3) * 100}
        showBack={false}
      >
        <JourneyLoadingScreen appId="payment-processing" message="Processando pagamento..." />
      </StandardJourneyBrandedScreen>
    );
  }

  if (quoteLoading) {
    return (
      <StandardJourneyBrandedScreen
        appId="journey-payment-loading"
        contentLayout="fullscreen"
        onBack={() => navigate(personalCoveragePath)}
        progressPercent={(2 / 3) * 100}
        showBack
      >
        <Box
          aria-busy="true"
          aria-live="polite"
          role="status"
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'center',
            minHeight: 240,
            width: '100%'
          }}
        >
          <JourneyLoadingScreen appId="payment-loading" message="Carregando dados do pagamento" />
        </Box>
      </StandardJourneyBrandedScreen>
    );
  }

  const methodIconSize = 64;

  const membershipInnerBlock = (
    <Box
      sx={{
        bgcolor: 'customColors.backgroundMute',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '36px',
        p: '16px',
        width: '100%'
      }}
    >
      <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
        <Typography sx={{ fontWeight: 600 }} variant="h6">
          Taxa de Adesão
        </Typography>
        <Typography color="primary" sx={{ fontWeight: 600 }} variant="h6">
          R$ {priceFormatter(membershipFee)}
        </Typography>
      </Box>
      <Box display="flex" flexDirection={{ sm: 'row', xs: 'column' }} gap={2} justifyContent="stretch" width="100%">
        {planOptions.membership_fee.methods.map((method) => (
          <MethodButton
            key={method}
            isSelected={membershipMethodSelected === method}
            variant="journey"
            onClick={() => setMembershipMethodSelected(method)}
          >
            {method === 'credit_card' && <AiOutlineCreditCard size={methodIconSize} />}
            {method === 'debit_card' && <AiOutlineCreditCard size={methodIconSize} />}
            {method === 'pix' && <FaPix size={methodIconSize} />}
            {method === 'boleto' && <AiOutlineCreditCard size={methodIconSize} />}
            {paymentMethodLabels[method] || method}
          </MethodButton>
        ))}
      </Box>
      {membershipMethodSelected === null && (
        <Typography color="error" sx={{ fontWeight: 600 }} variant="body2">
          Selecione uma forma de pagamento
        </Typography>
      )}
      {membershipMethodSelected === 'credit_card' ? (
        <CardForm
          formMethods={formMethods}
          focused={focused}
          setFocused={setfocused}
          checkedTermOfContract={checkedTermOfContract}
          handleSubmit={formMethods.handleSubmit(handleMembershipFeeSubmit)}
          loading={loading || (!!organizationIdForPayment && paymentGatewayKeyLoading)}
          showCard={showCard}
          paymentError={paymentError}
          stepSelected={stepSelected}
          onConfirmStep={(currentStep) => handleConfirmStep(currentStep as PaymentStep)}
          paymentStepsState={paymentStepsState}
          setCheckedTermOfContract={setCheckedTermOfContract}
          organizationId={organizationIdForPayment}
          submitButtonLabel="Confirmar pagamento"
          uiVariant="journey"
        />
      ) : null}
      {membershipMethodSelected === 'pix' ? (
        <PixPayment
          pixValue={membershipFee}
          qrCodeValue={pixValue}
          setQrCodeValue={setPixValue}
          onGenerateQRCode={generatePixQRCode}
          timeLeft={timeLeft}
          paymentConfirmed={pixPaymentConfirmed}
          disableGenerateButton={!quoteIdForApi || quoteLoading}
          generating={pixGenerating}
        />
      ) : null}
      {membershipMethodSelected === 'pix' && pixValue ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: pixPaymentConfirmed ? 'column' : 'row',
            gap: 2,
            justifyContent: 'center',
            mt: 1,
            width: '100%'
          }}
        >
          {pixPaymentConfirmed ? (
            <>
              <Typography
                color="success.main"
                sx={{ alignItems: 'center', display: 'flex', fontWeight: 600, gap: 0.5 }}
                variant="subtitle1"
              >
                <svg
                  aria-hidden
                  fill="none"
                  height="24"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  width="24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12l2 2 4-4" />
                </svg>
                Pagamento confirmado
              </Typography>
              <Button color="primary" onClick={() => handleConfirmStep('membership_fee')} variant="contained">
                Continuar para o próximo passo
              </Button>
            </>
          ) : (
            <LoadingButton
              disabled={!pixValue || timeLeft <= 0 || !!eventSourceConnection || pixPaymentConfirmed}
              loading={!!eventSourceConnection}
              onClick={async () => {
                try {
                  if (!pixId || !!eventSourceConnection) {
                    return;
                  }
                  setLoading(true);
                  await waitForPixPaymentConfirmation(pixId);
                } catch (error) {
                  const err = error as Error;
                  reportError('Erro ao verificar pagamento via Pix', error, {
                    context: 'pixPayment_button_verification',
                    pixId,
                    hasPixValue: !!pixValue
                  });
                  storeState.addToast({
                    title: 'Erro ao verificar pagamento via Pix',
                    message: err.message || 'Não foi possível verificar o status do pagamento via Pix.',
                    type: 'error'
                  });
                } finally {
                  setLoading(false);
                }
              }}
              variant="contained"
            >
              {eventSourceConnection ? 'Verificando Pagamento…' : 'Confirmar Pagamento'}
            </LoadingButton>
          )}
        </Box>
      ) : null}
    </Box>
  );

  const installmentsMethodBlock = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderColor: 'divider',
          borderRadius: 2,
          borderStyle: 'solid',
          borderWidth: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          width: '100%'
        }}
      >
        <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
          <Typography sx={{ fontWeight: 600 }} variant="h6">
            Mensalidade
          </Typography>
          <Typography color="primary" sx={{ fontWeight: 600 }} variant="h6">
            R${priceFormatter(totalValue)}/mês
          </Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary' }} variant="body2">
          {installmentsMethodSelected === 'pix'
            ? 'Pagamento único via PIX.'
            : installmentsSectionLocked
            ? 'Pagamento recorrente via cartão de crédito.'
            : 'Cobrança mensal automática no cartão de crédito.'}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', width: '100%' }}>
        {installmentsSectionLocked ? (
          <Box
            aria-hidden
            sx={{
              alignItems: 'center',
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.72)'),
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              inset: 0,
              justifyContent: 'center',
              minHeight: 200,
              position: 'absolute',
              zIndex: 2
            }}
          >
            <LockOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography sx={{ fontWeight: 600, maxWidth: 280, textAlign: 'center' }} variant="subtitle1">
              Complete o Passo 1 para desbloquear
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            opacity: installmentsSectionLocked ? 0.35 : 1,
            pointerEvents: installmentsSectionLocked ? 'none' : 'auto'
          }}
        >
          <Box
            display="flex"
            flexDirection={{ sm: 'row', xs: 'column' }}
            gap={2}
            justifyContent="stretch"
            mb={2}
            width="100%"
          >
            {planOptions.installments.methods.map((method) => (
              <MethodButton
                key={method}
                isSelected={installmentsMethodSelected === method}
                variant="journey"
                onClick={() => !installmentsSectionLocked && setInstallmentsMethodSelected(method)}
              >
                {method === 'credit_card' && <AiOutlineCreditCard size={methodIconSize} />}
                {method === 'debit_card' && <AiOutlineCreditCard size={methodIconSize} />}
                {method === 'pix' && <FaPix size={methodIconSize} />}
                {method === 'boleto' && <AiOutlineCreditCard size={methodIconSize} />}
                {paymentMethodLabels[method] || method}
              </MethodButton>
            ))}
          </Box>
          {(!hasMembershipFee || stepSelected === 'installments') &&
          installmentsMethodSelected === null &&
          !installmentsSectionLocked ? (
            <Typography color="error" sx={{ fontWeight: 600, mb: 2 }} variant="body2">
              Selecione uma forma de pagamento
            </Typography>
          ) : null}
          {(!hasMembershipFee || stepSelected === 'installments') && installmentsMethodSelected === 'credit_card' ? (
            <>
              {planOptions.membership_fee.methods.includes('credit_card') && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={reuseCardData}
                        color="success"
                        disabled={!planOptions.membership_fee.methods.includes('credit_card')}
                        onChange={(e) => setReuseCardData(e.target.checked)}
                      />
                    }
                    label="Reutilizar os dados do cartão usado na taxa de adesão"
                  />
                </Box>
              )}
              {reuseCardData ? (
                <Box sx={{ mb: 2 }}>
                  <Button color="primary" onClick={handleReusedCardSubmit} variant="contained">
                    Efetuar pagamento com os dados reutilizados
                  </Button>
                </Box>
              ) : (
                <CardForm
                  formMethods={formMethods}
                  focused={focused}
                  setFocused={setfocused}
                  checkedTermOfContract={checkedTermOfContract}
                  handleSubmit={formMethods.handleSubmit(handleInstallmentsSubmit)}
                  loading={loading || (!!organizationIdForPayment && paymentGatewayKeyLoading)}
                  showCard={showCard}
                  paymentError={paymentError}
                  stepSelected={stepSelected}
                  onConfirmStep={(currentStep) => handleConfirmStep(currentStep as PaymentStep)}
                  paymentStepsState={paymentStepsState}
                  setCheckedTermOfContract={setCheckedTermOfContract}
                  organizationId={organizationIdForPayment}
                  submitButtonLabel="Confirmar pagamento"
                  uiVariant="journey"
                />
              )}
            </>
          ) : null}
          {(!hasMembershipFee || stepSelected === 'installments') && installmentsMethodSelected === 'pix' ? (
            <Typography sx={{ mt: 1 }} variant="body1">
              Fique tranquilo(a),{' '}
              <Typography component="span" sx={{ color: theme.palette.primary.main }}>
                {customer.name}
              </Typography>
              ! Após o pagamento, você receberá um email em{' '}
              <Typography component="span" sx={{ color: theme.palette.primary.main }}>
                {customer.email}
              </Typography>{' '}
              com a confirmação e dados da contratação.
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );

  return (
    <StandardJourneyBrandedScreen
      appId="journey-payment"
      checkoutActiveStep={1}
      contentLayout="checkout"
      onBack={() => navigate(personalCoveragePath)}
      progressPercent={(2 / 3) * 100}
      showBack
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <JourneyQuoteInsuredHeader
          document={insured.document}
          equipmentLabel={equipmentLabel}
          equipmentValue={equipmentValueNumber}
          name={displayInsuredName}
        />
        <Grid container spacing={4}>
          <Grid item lg={8} md={7} xs={12}>
            <Typography align="center" component="h1" sx={{ fontWeight: 600, mb: 3 }} variant="h5">
              Complete o pagamento em dois passos simples
            </Typography>

            {hasMembershipFee ? (
              <PaymentJourneyStepCard stepLabel="Passo 1" title={membershipStepTitle}>
                {!paymentStepsState.membership_fee ? (
                  <>
                    <Typography sx={{ fontWeight: 500, mb: 2 }} variant="subtitle1">
                      Para ativar sua proteção, é necessário pagar a taxa de adesão.
                    </Typography>
                    {membershipInnerBlock}
                  </>
                ) : (
                  <Box
                    data-app-id="payment-membership-confirmed"
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      py: 2,
                      textAlign: 'center'
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        alignItems: 'center',
                        bgcolor: 'success.main',
                        borderRadius: '100px',
                        color: 'success.contrastText',
                        display: 'flex',
                        height: 40,
                        justifyContent: 'center',
                        width: 40
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600 }} variant="subtitle1">
                      Pagamento confirmado com sucesso!
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }} variant="caption">
                      Agora você pode configurar seu cartão de crédito.
                    </Typography>
                  </Box>
                )}
              </PaymentJourneyStepCard>
            ) : null}

            <PaymentJourneyStepCard
              stepLabel={hasMembershipFee ? 'Passo 2' : 'Pagamento'}
              title={hasMembershipFee ? 'Faça sua assinatura' : 'Pagamento da mensalidade'}
            >
              <Typography sx={{ fontWeight: 500, mb: 2 }} variant="subtitle1">
                {hasMembershipFee
                  ? 'Configure seu cartão de crédito para o pagamento mensal da proteção.'
                  : 'Escolha a forma de pagamento e conclua a sua assinatura.'}
              </Typography>
              {installmentsMethodBlock}
            </PaymentJourneyStepCard>
          </Grid>
          <Grid item lg={4} md={5} xs={12}>
            <JourneyQuoteSummaryColumn
              advanceLabel="Avançar"
              advancing={false}
              disableAdvance
              membershipFee={membershipFee ?? 0}
              membershipPaid={Boolean(paymentStepsState.membership_fee)}
              onAdvance={() => {}}
              productsLoading={false}
              serviceChargeFormatted={priceFormatter(serviceCharge)}
              showAdvanceButton={false}
              showOperationalRow={!isOnlyProduct9}
              subscriptionRows={subscriptionRows}
              totalMonthlyNumber={totalValue}
            />
          </Grid>
        </Grid>
      </Box>
    </StandardJourneyBrandedScreen>
  );
}
